import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import { Prod01 } from '../../constants/ActionType';
import { getTimeReportCCM } from '../../actions/HRActions';
import ProgramBillingPanel from './ProgramBillingPanel';
import { ClinicalNoteRow, PcmEnrollPrompt } from './ClinicalNote';
import { formatDateTime } from '../../utils/dateFormat';

// CCM time -> CPT coding panel: nurse Start/Stop timer + manual minutes fallback, per-patient
// calendar-month minutes with staff attribution, threshold coaching, neutral 60-min review prompt,
// monthly complexity attestation (billing provider), and the resulting billing OPPORTUNITY
// (not a claim). Talks to /ccm/* on the beta backend.

const COLORS = { red: '#dc3545', amber: '#ff9800', green: '#28a745' };
const NURSE_ROLES = ['HrMedical', 'HrLead', 'HrNonMedical'];
const ATTEST_ROLES = ['doctor', 'admin', 'adminStaff'];
const READ_ONLY_ROLES = ['billing', 'accounting'];
const CAP_OVERRIDE_ROLES = ['adminStaff', 'admin'];
const MDM_COLS = ['problems', 'data', 'risk'];
const money = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : 'n/a');
const timerKey = (patientId) => `ccmTimer:${patientId}`;

const fmt = (secs) => {
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
    return `${h ? `${h}:` : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};
const codeStr = (codes) => (codes && codes.length ? codes.map(c => `${c.code}${c.units > 1 ? ` ×${c.units}` : ''}`).join(' + ') : 'none yet');

const CCMCodingPanel = ({ patientId }) => {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth);
    const role = user?.role;
    const canLogTime = !!role && !READ_ONLY_ROLES.includes(role);
    const canAttest = ATTEST_ROLES.includes(role);
    const canOverrideCap = CAP_OVERRIDE_ROLES.includes(role);
    const isNurse = NURSE_ROLES.includes(role);

    const now = moment();
    const [year, setYear] = useState(now.year());
    const [month, setMonth] = useState(now.month() + 1);
    const isCurrentMonth = year === now.year() && month === now.month() + 1;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    // timer (persisted so a page reload does not lose the running start time)
    const [startedAt, setStartedAt] = useState(() => localStorage.getItem(timerKey(patientId)) || null);
    const [elapsed, setElapsed] = useState(0);
    const tick = useRef(null);

    // manual fallback
    const [manualMinutes, setManualMinutes] = useState('');
    const [note, setNote] = useState('');
    // no program default anywhere (owner decision 2026-09-26): the staff member must pick one per entry
    const [program, setProgram] = useState('');
    const [enrolledPrograms, setEnrolledPrograms] = useState([]);
    const [gateMode, setGateMode] = useState('enforce');
    const [enrollWarning, setEnrollWarning] = useState(null);
    const [pcmPrompt, setPcmPrompt] = useState(null);
    const [interactive, setInteractive] = useState(null);
    const [oversight, setOversight] = useState(false);

    // attestation
    const [complexChoice, setComplexChoice] = useState('non-complex');
    const [justification, setJustification] = useState('');
    const [attestNote, setAttestNote] = useState('');
    const [providerId, setProviderId] = useState('');
    const [providers, setProviders] = useState([]);
    const [mdmElements, setMdmElements] = useState({ problems: [], data: [], risk: [] });
    const [capUnits, setCapUnits] = useState('3');
    const [capProgram, setCapProgram] = useState('CCM');
    const [capJustification, setCapJustification] = useState('');
    const [showEntries, setShowEntries] = useState(false);
    const [billingRefresh, setBillingRefresh] = useState(0);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const [res, billing] = await Promise.all([
                axios.get(`${Prod01}/ccm/month/${patientId}?year=${year}&month=${month}`),
                axios.get(`${Prod01}/billing/determination/${patientId}?year=${year}&month=${month}`),
            ]);
            setData(res.data);
            const enrolled = billing.data?.enrolledPrograms || [];
            setEnrolledPrograms(enrolled);
            const mode = billing.data?.enrollmentGate?.mode === 'warn' ? 'warn' : 'enforce';
            setGateMode(mode);
            setProgram(current => (mode === 'warn' || enrolled.includes(current) ? current : ''));
            setPcmPrompt(billing.data?.pcmEnrollmentPrompt?.show ? billing.data.pcmEnrollmentPrompt : null);
            setComplexChoice(res.data?.complexity?.complex ? 'complex' : 'non-complex');
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally { setLoading(false); }
    }, [patientId, year, month]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!canAttest || role === 'doctor') return;
        axios.get(`${Prod01}/doctor/list/100/1`).then(r => {
            const list = Array.isArray(r.data) ? r.data : (r.data?.results || r.data?.doctors || []);
            setProviders(list);
        }).catch(() => setProviders([]));
    }, [canAttest, role]);

    useEffect(() => {
        if (!startedAt) { setElapsed(0); return undefined; }
        const run = () => setElapsed(Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000)));
        run();
        tick.current = setInterval(run, 1000);
        return () => clearInterval(tick.current);
    }, [startedAt]);

    const startTimer = () => {
        const s = new Date().toISOString();
        localStorage.setItem(timerKey(patientId), s);
        setStartedAt(s); setNotice(null); setError(null);
    };
    const discardTimer = () => {
        localStorage.removeItem(timerKey(patientId));
        setStartedAt(null);
    };
    const stopTimer = async () => {
        if (!startedAt) return;
        const endedAt = new Date().toISOString();
        if (Date.now() - new Date(startedAt).getTime() < 30000) {
            setError('Timer ran less than 30 seconds - nothing logged. Use the minutes field if you need to log time manually.');
            discardTimer();
            return;
        }
        await post({ source: 'timer', startedAt, endedAt, note, interactive, physicianOversight: oversight }, () => discardTimer());
    };

    const post = async (body, after) => {
        if (!note.trim()) { setError('A note is required for every time entry.'); return; }
        if (!program) { setError('Select the program (CCM, PCM, RPM or APCM) for this entry - there is no default.'); return; }
        if (program === 'RPM' && typeof interactive !== 'boolean') {
            setError('Choose interactive or non-interactive for RPM.'); return;
        }
        if (!enrolledPrograms.includes(program) && gateMode !== 'warn') { setPcmPrompt({ program, show: true }); setError(`Enroll first: the patient is not actively enrolled in ${program}.`); return; }
        setLoading(true); setError(null); setNotice(null); setEnrollWarning(null);
        try {
            const res = await axios.post(`${Prod01}/ccm/entry/${patientId}`, { ...body, program, note: note.trim(), interactive: program === 'RPM' ? interactive : undefined });
            const e = res.data.entry;
            setNotice(`Logged ${e.minutes} min (${e.source}) by ${e.loggedByName} for ${moment(e.loggedAt).format('MMM YYYY')}.`);
            setEnrollWarning(res.data.warning?.message || null);
            if (after) after();
            setNote('');
            if (e.year === year && e.month === month) setData(res.data.month); else load();
            setBillingRefresh(k => k + 1);
            dispatch(getTimeReportCCM(patientId, String(month).padStart(2, '0'), String(year)));
        } catch (err) {
            const details = err?.response?.data?.details;
            if (details?.code === 'PCM_NOT_ENROLLED' || details?.code === 'NOT_ENROLLED') setPcmPrompt({ ...details, program: details.program || program, show: true });
            setError(err?.response?.data?.message || err.message);
        } finally { setLoading(false); }
    };

    const submitManual = async () => {
        const m = parseInt(manualMinutes, 10);
        if (!m || m < 1) { setError('Enter whole minutes (1 or more).'); return; }
        const loggedAt = isCurrentMonth ? undefined : moment({ year, month: month - 1 }).endOf('month').toISOString();
        await post({ source: 'manual', minutes: m, note, interactive, physicianOversight: oversight, loggedAt }, () => setManualMinutes(''));
    };

    const submitAttestation = async () => {
        const complex = complexChoice === 'complex';
        if (complex && !justification) { setError('Select the complexity justification.'); return; }
        if (complex && MDM_COLS.filter(c => mdmElements[c].length > 0).length < 2) { setError('Select the MDM elements met in at least 2 of the 3 columns (Moderate or High required).'); return; }
        if (role !== 'doctor' && !providerId) { setError('Select the billing provider who made this determination.'); return; }
        setLoading(true); setError(null); setNotice(null);
        try {
            const res = await axios.post(`${Prod01}/ccm/complexity/${patientId}`, {
                year, month, complex, justification: complex ? justification : undefined, note: attestNote || undefined,
                mdm: complex ? { elements: mdmElements } : undefined,
                providerId: role !== 'doctor' ? providerId : undefined,
            });
            setData(res.data.month);
            setNotice(`Complexity for ${moment({ year, month: month - 1 }).format('MMMM YYYY')} recorded as ${complex ? 'COMPLEX' : 'non-complex'}.`);
            setAttestNote(''); setJustification(''); setMdmElements({ problems: [], data: [], risk: [] });
        } catch (err) {
            setError(err?.response?.data?.message || err.message);
        } finally { setLoading(false); }
    };

    const toggleMdm = (col, key) => setMdmElements(prev => ({ ...prev, [col]: prev[col].includes(key) ? prev[col].filter(k => k !== key) : [...prev[col], key] }));

    const submitCapOverride = async (approved) => {
        if (approved && capJustification.trim().length < 10) { setError('Documented justification (at least 10 characters) is required for an add-on cap override.'); return; }
        setLoading(true); setError(null); setNotice(null);
        try {
            const res = await axios.post(`${Prod01}/ccm/cap-override/${patientId}`, {
                year, month, program: capProgram, approved, units: approved ? parseInt(capUnits, 10) : undefined, justification: approved ? capJustification.trim() : undefined,
            });
            setData(res.data.month || res.data);
            setNotice(approved ? `${capProgram === 'PCM' ? '99427' : '99489'} soft-cap override approved (x${capUnits}) for ${moment({ year, month: month - 1 }).format('MMMM YYYY')} - logged.` : 'Cap override revoked.');
            setCapJustification('');
            setBillingRefresh(k => k + 1);
        } catch (err) {
            setError(err?.response?.data?.message || err.message);
        } finally { setLoading(false); }
    };

    const coding = data?.coding;
    const ledger = data?.ledger;
    const capOverride = capProgram === 'PCM' ? data?.pcmCapOverride : data?.capOverride;
    const mdmGrid = data?.complexity?.mdmGrid;
    const mdmSummary = data?.complexity?.attestation?.mdm;
    const coach = coding?.coaching;
    const minutes = data?.minutes;
    const complexity = data?.complexity;
    const projectedManual = parseInt(manualMinutes, 10) || 0;
    const monthLabel = moment({ year, month: month - 1 }).format('MMMM YYYY');
    const monthOptions = Array.from({ length: 12 }, (_, i) => moment().subtract(i, 'months'));

    return (
        <div className="card mb-3" style={{ border: '1px solid #dee2e6' }}>
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <h5 className="mb-0">CCM Time &amp; Coding Opportunity</h5>
                    <select className="form-select form-select-sm" style={{ width: 'auto' }}
                        value={`${year}-${month}`}
                        onChange={e => { const [y, m] = e.target.value.split('-').map(Number); setYear(y); setMonth(m); }}>
                        {monthOptions.map(d => <option key={d.format('YYYY-M')} value={`${d.year()}-${d.month() + 1}`}>{d.format('MMMM YYYY')}</option>)}
                    </select>
                </div>
                <small className="text-muted">Billing opportunity suggestion only - not a claim. Requires billing-team validation.</small>

                {error && <div className="alert alert-danger py-2 mt-2 mb-2">{error}</div>}
                {notice && <div className="alert alert-success py-2 mt-2 mb-2">{notice}</div>}
                {enrollWarning && <div className="alert alert-warning py-2 mt-2 mb-2">{enrollWarning}</div>}
                {loading && !data && <div className="mt-2">Loading...</div>}

                {data && (
                    <div className="row mt-3">
                        {/* minutes + coaching */}
                        <div className="col-md-4">
                            <div className="p-2 rounded" style={{ background: '#f8f9fa' }}>
                                <div style={{ fontSize: 13, color: '#666' }}>{monthLabel} · {coding.track} track · rules {coding.ruleSet}</div>
                                <div style={{ fontSize: 30, fontWeight: 700 }}>{minutes.staffMinutes} <span style={{ fontSize: 14, fontWeight: 400 }}>staff min</span></div>
                                {minutes.physicianMinutes > 0 && <div style={{ fontSize: 13 }}>+ {minutes.physicianMinutes} physician-logged min</div>}
                                {coding.minuteSummary && (
                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{coding.minuteSummary}</div>
                                )}
                                {coach && (
                                    <div className="mt-2 px-2 py-1 rounded text-white d-inline-block" style={{ background: COLORS[coach.color] || '#6c757d', fontWeight: 600 }}>
                                        {coach.message}
                                    </div>
                                )}
                                <div className="mt-2" style={{ fontSize: 13 }}>
                                    <strong>Selected ({coding.track}):</strong> {codeStr(coding.codes)}{typeof coding.expectedAmount === 'number' ? ` · ${money(coding.expectedAmount)}` : ''}
                                </div>
                                {coding.eitherOr && coding.alternative && (
                                    <div className="px-2 py-1 mt-1 rounded" style={{ fontSize: 12, background: '#fff3cd', color: '#856404' }}>
                                        <b>Either/or:</b> staff and physician CCM codes are bundled - never both in one month. Excluded alternative: {codeStr(coding.alternative.codes)} ({coding.alternative.track}{typeof coding.alternative.amount === 'number' ? `, ${money(coding.alternative.amount)}` : ''}). Higher value selected by default; biller confirms.
                                    </div>
                                )}
                                {coding.physician?.minutes > 0 && !coding.physician.emitted && !coding.eitherOr && (
                                    <div className="text-muted" style={{ fontSize: 12 }}>Physician-logged {coding.physician.minutes} min - no physician code threshold reached.</div>
                                )}
                                {coding.softCap && (coding.softCap.capped || coding.softCap.overrideUnits) && (
                                    <div className="px-2 py-1 mt-1 rounded" style={{ fontSize: 12, background: coding.softCap.capped ? '#fff3cd' : '#e8f5e9', color: coding.softCap.capped ? '#856404' : '#155724' }}>
                                        <b>99489 soft cap:</b> {coding.softCap.rawUnits} unit(s) by time, {coding.softCap.emittedUnits} auto-suggested (cap {coding.softCap.cap}).
                                        {coding.softCap.capped ? ' Exceeds 99489 soft cap - units beyond 2 need an org-manager override with documented justification.' : ` Override approved x${coding.softCap.overrideUnits}.`}
                                    </div>
                                )}
                                {(coding.flags || []).filter(f => !['CCM_STAFF_PHYSICIAN_EITHER_OR', 'CCM_99489_SOFT_CAP'].includes(f.key)).map(f => (
                                    <div key={f.key} className="text-muted" style={{ fontSize: 12 }}>{f.message}</div>
                                ))}
                                {data.physicianOversightNote && <div className="text-muted mt-1" style={{ fontSize: 12 }}>{data.physicianOversightNote}</div>}
                                {Object.keys(minutes.byStaff || {}).length > 0 && (
                                    <div className="mt-2" style={{ fontSize: 12 }}>
                                        <strong>By staff:</strong> {Object.entries(minutes.byStaff).map(([k, v]) => `${k} ${v}m`).join(' · ')}
                                    </div>
                                )}
                                <button className="btn btn-link btn-sm p-0 mt-1" onClick={() => setShowEntries(s => !s)}>
                                    {showEntries ? 'Hide' : 'Show'} entries ({minutes.rows.length})
                                </button>
                                {showEntries && (
                                    <ul className="list-unstyled mb-0 mt-1" style={{ fontSize: 12, maxHeight: 160, overflowY: 'auto' }}>
                                        {minutes.rows.map(r => (
                                            <ClinicalNoteRow key={r.id} row={r} user={user} onChanged={load}>
                                                {formatDateTime(r.loggedAt)} · {r.minutes}m · {r.source}{r.program ? ` · ${r.program}` : ''} · {r.loggedBy || '—'}{r.physician ? ' (physician)' : ''}{r.note ? ` · ${r.note}` : ''}
                                            </ClinicalNoteRow>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {coding.reviewPrompt && (
                                <div className="alert alert-info py-2 mt-2 mb-0" style={{ fontSize: 13 }}>
                                    <strong>Complexity review:</strong> {coding.reviewPrompt}
                                </div>
                            )}
                        </div>

                        {/* timer + manual */}
                        <div className="col-md-4">
                            {canLogTime ? (
                                <>
                                    <div className="p-2 rounded" style={{ background: '#f8f9fa' }}>
                                        <div style={{ fontSize: 13, color: '#666' }}>Timer (logs to the current month as {user?.firstname} {user?.lastname})</div>
                                        <div style={{ fontSize: 28, fontFamily: 'monospace' }}>{fmt(elapsed)}</div>
                                        {!startedAt ? (
                                            <button className="btn btn-success btn-sm" onClick={startTimer} disabled={loading}>▶ Start</button>
                                        ) : (
                                            <>
                                                <button className="btn btn-danger btn-sm me-2" onClick={stopTimer} disabled={loading}>■ Stop &amp; log</button>
                                                <button className="btn btn-outline-secondary btn-sm" onClick={discardTimer} disabled={loading}>Discard</button>
                                            </>
                                        )}
                                    </div>
                                    <div className="p-2 rounded mt-2" style={{ background: '#f8f9fa' }}>
                                        <div style={{ fontSize: 13, color: '#666' }}>Minutes (fallback when the timer wasn't used) → {monthLabel}</div>
                                        <div className="d-flex align-items-center">
                                            <input type="number" min="1" max="600" className="form-control form-control-sm me-2" style={{ width: 90 }}
                                                value={manualMinutes} onChange={e => setManualMinutes(e.target.value)} placeholder="min" />
                                            <button className="btn btn-primary btn-sm" onClick={submitManual} disabled={loading || !manualMinutes || !note.trim() || !program || (program === 'RPM' && interactive === null)}>Log minutes</button>
                                        </div>
                                        {projectedManual > 0 && coach && (
                                            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                                After logging: {minutes.staffMinutes + projectedManual} min this month
                                            </div>
                                        )}
                                        <select className="form-select form-select-sm mt-2" aria-label="Care management program" value={program} required onChange={e => { setProgram(e.target.value); setInteractive(null); }}>
                                            <option value="">Select program (required)</option>
                                            {[...new Set([...enrolledPrograms, 'CCM', 'PCM', 'RPM', 'APCM'])].map(p => <option key={p} value={p}>{p}{enrolledPrograms.includes(p) ? '' : ' (not enrolled)'}</option>)}
                                        </select>
                                        {pcmPrompt && canLogTime && (
                                            <PcmEnrollPrompt patientId={patientId} prompt={pcmPrompt} onEnrolled={() => { setPcmPrompt(null); setError(null); load(); setBillingRefresh(k => k + 1); }} onDismiss={() => setPcmPrompt(null)} />
                                        )}
                                        <input className="form-control form-control-sm mt-2" placeholder="Note (required)" required value={note} onChange={e => setNote(e.target.value)} />
                                        {program === 'RPM' && (
                                            <select className="form-select form-select-sm mt-1" aria-label="RPM interactive contact" value={interactive === null ? '' : String(interactive)} onChange={e => setInteractive(e.target.value === '' ? null : e.target.value === 'true')}>
                                                <option value="">Select interactive status</option>
                                                <option value="true">Interactive</option>
                                                <option value="false">Non-interactive</option>
                                            </select>
                                        )}
                                        <div className="form-check mt-1">
                                            <input className="form-check-input" type="checkbox" id={`ccmInteractive-${patientId}`} checked={!!interactive} onChange={e => setInteractive(e.target.checked)} />
                                            <label className="form-check-label" htmlFor={`ccmInteractive-${patientId}`} style={{ fontSize: 12 }}>Interactive communication</label>
                                        </div>
                                        {isNurse && (
                                            <div className="form-check">
                                                <input className="form-check-input" type="checkbox" id={`ccmOversight-${patientId}`} checked={oversight} onChange={e => setOversight(e.target.checked)} />
                                                <label className="form-check-label" htmlFor={`ccmOversight-${patientId}`} style={{ fontSize: 12 }}>
                                                    Physician oversight <span className="text-muted">(does not create a physician-code opportunity; only physician-logged time does)</span>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="text-muted" style={{ fontSize: 13 }}>Read-only role: time logging not available.</div>
                            )}
                        </div>

                        {/* complexity attestation */}
                        <div className="col-md-4">
                            <div className="p-2 rounded" style={{ background: '#f8f9fa' }}>
                                <div style={{ fontSize: 13, color: '#666' }}>Complexity for {monthLabel}</div>
                                <div style={{ fontWeight: 700 }}>
                                    {complexity.complex ? <span style={{ color: COLORS.amber }}>COMPLEX (attested)</span> : <span>Non-complex (default)</span>}
                                </div>
                                {complexity.attestation?.attestedAt ? (
                                    <div style={{ fontSize: 12 }}>
                                        Determined by {complexity.attestation.determinedByName} on {formatDateTime(complexity.attestation.attestedAt)}
                                        {complexity.attestation.justification && <> · {complexity.justifications.find(j => j.key === complexity.attestation.justification)?.label || complexity.attestation.justification}</>}
                                        {mdmSummary?.level && (
                                            <div>MDM level <b>{mdmSummary.level.toUpperCase()}</b> (problems {mdmSummary.columns?.problems}, data {mdmSummary.columns?.data}, risk {mdmSummary.columns?.risk}) · elements: {MDM_COLS.map(c => (mdmSummary.elements?.[c] || []).length).join('/')}</div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-muted" style={{ fontSize: 12 }}>No attestation recorded this month. Complexity is an MDM determination by the billing provider and must be re-affirmed each calendar month; time alone never changes it.</div>
                                )}
                                {canAttest && (
                                    <div className="mt-2">
                                        <select className="form-select form-select-sm mb-1" value={complexChoice} onChange={e => setComplexChoice(e.target.value)}>
                                            <option value="non-complex">Non-complex CCM (99490/99439)</option>
                                            <option value="complex">Complex CCM (99487/99489)</option>
                                        </select>
                                        {complexChoice === 'complex' && (
                                            <>
                                                <select className="form-select form-select-sm mb-1" value={justification} onChange={e => setJustification(e.target.value)}>
                                                    <option value="">Justification (required)…</option>
                                                    {complexity.justifications.map(j => <option key={j.key} value={j.key}>{j.label}</option>)}
                                                </select>
                                                {mdmGrid && (
                                                    <div className="p-1 mb-1 rounded" style={{ border: '1px solid #ced4da', fontSize: 11, maxHeight: 260, overflowY: 'auto' }}>
                                                        <div className="fw-bold">AMA MDM grid - tick the elements met. A level is met when 2 of 3 columns reach it; only Moderate or High unlocks complex CCM. Minutes are never a reason.</div>
                                                        {MDM_COLS.map(col => (
                                                            <div key={col} className="mt-1">
                                                                <div className="fw-bold text-capitalize">{mdmGrid[col]?.label || col}</div>
                                                                {(mdmGrid[col]?.elements || []).map(el => (
                                                                    <div className="form-check" key={el.key}>
                                                                        <input className="form-check-input" type="checkbox" id={`mdm-${patientId}-${el.key}`} checked={mdmElements[col].includes(el.key)} onChange={() => toggleMdm(col, el.key)} />
                                                                        <label className="form-check-label" htmlFor={`mdm-${patientId}-${el.key}`}><span className="badge bg-secondary me-1">{el.level}</span>{el.label}</label>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {role !== 'doctor' && (
                                            <select className="form-select form-select-sm mb-1" value={providerId} onChange={e => setProviderId(e.target.value)}>
                                                <option value="">Billing provider who determined this…</option>
                                                {providers.map(d => <option key={d._id} value={d._id}>{d.firstname} {d.lastname}</option>)}
                                            </select>
                                        )}
                                        <input className="form-control form-control-sm mb-1" placeholder="Note (optional)" value={attestNote} onChange={e => setAttestNote(e.target.value)} />
                                        <button className="btn btn-outline-primary btn-sm" onClick={submitAttestation} disabled={loading}>
                                            Record attestation for {monthLabel}
                                        </button>
                                    </div>
                                )}
                                {complexity.history?.length > 0 && (
                                    <details className="mt-2" style={{ fontSize: 12 }}>
                                        <summary>Audit trail ({complexity.history.length})</summary>
                                        {complexity.history.map((h, i) => (
                                            <div key={i}>{formatDateTime(h.at)} · {h.complex ? `COMPLEX${h.mdm?.level ? ` (MDM ${h.mdm.level})` : ''}` : 'non-complex'} · {h.determinedByName}{h.justification ? ` · ${h.justification}` : ''}</div>
                                        ))}
                                    </details>
                                )}
                            </div>
                            <div className="p-2 rounded mt-2" style={{ background: '#f8f9fa' }}>
                                <div style={{ fontSize: 13, color: '#666' }}>{capProgram === 'PCM' ? '99427' : '99489'} soft-cap override - {monthLabel}</div>
                                <div style={{ fontSize: 12 }}>
                                    {capOverride?.current ? (
                                        <span style={{ color: '#155724' }}>Approved x{capOverride.current.units} by {capOverride.current.approvedByName} ({capOverride.current.approvedByRole}) on {formatDateTime(capOverride.current.approvedAt)} · {capOverride.current.justification}</span>
                                    ) : (
                                        <span className="text-muted">{capOverride?.note || 'No override this month.'}</span>
                                    )}
                                </div>
                                {canOverrideCap ? (
                                    <div className="mt-1">
                                        <select className="form-select form-select-sm mb-1" value={capProgram} onChange={e => setCapProgram(e.target.value)}>
                                            <option value="CCM">CCM 99489</option><option value="PCM">PCM 99427</option>
                                        </select>
                                        <div className="d-flex gap-1">
                                            <select className="form-select form-select-sm" style={{ width: 'auto' }} value={capUnits} onChange={e => setCapUnits(e.target.value)}>
                                                {Array.from({ length: 10 }, (_, i) => i + 3).map(n => <option key={n} value={n}>{capProgram === 'PCM' ? '99427' : '99489'} x{n}</option>)}
                                            </select>
                                            <input className="form-control form-control-sm" placeholder="Documented justification (required)" value={capJustification} onChange={e => setCapJustification(e.target.value)} />
                                        </div>
                                        <div className="d-flex gap-1 mt-1">
                                            <button className="btn btn-outline-warning btn-sm" onClick={() => submitCapOverride(true)} disabled={loading}>Approve override</button>
                                            {capOverride?.current && <button className="btn btn-outline-secondary btn-sm" onClick={() => submitCapOverride(false)} disabled={loading}>Revoke</button>}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-muted" style={{ fontSize: 11 }}>Only an org manager can approve units above the cap (nurse / billing / provider cannot self-approve).</div>
                                )}
                                {capOverride?.history?.length > 0 && (
                                    <details className="mt-1" style={{ fontSize: 11 }}>
                                        <summary>Override log ({capOverride.history.length})</summary>
                                        {capOverride.history.map((h, i) => (
                                            <div key={i}>{formatDateTime(h.at || h.approvedAt)} · {h.approved ? `APPROVED x${h.units}` : 'REVOKED'} · {h.approvedByName} ({h.approvedByRole}){h.justification ? ` · ${h.justification}` : ''}</div>
                                        ))}
                                    </details>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {ledger && (
                    <div className="mt-2 p-2 rounded" style={{ background: '#f8f9fa', fontSize: 13 }}>
                        <b>Care-management ledger · {ledger.totalMinutes} physical minutes</b>
                        {Object.entries(ledger.programs).map(([name, summary]) => (
                            <div key={name}>{name}: {summary.totalMinutes} min total
                                {name === 'RPM' && ` (interactive ${summary.interactiveMinutes} · non-interactive ${summary.nonInteractiveMinutes})`}
                                {' · '}{summary.billableMinutes ?? 0} billable · {summary.nonBillableMinutes ?? summary.totalMinutes} non-billable — {codeStr(summary.codes)}
                                {name === 'RPM' && `; device days ${summary.deviceDays} → ${summary.deviceCode || 'none'}`}
                                {(summary.flags || []).map(flag => <span key={flag.key} className="text-warning"> · {flag.message}</span>)}
                            </div>
                        ))}
                    </div>
                )}
                <ProgramBillingPanel patientId={patientId} year={year} month={month} onEnrollmentChanged={load} refreshKey={billingRefresh + (data?.minutes?.staffMinutes || 0) + (data?.complexity?.complex ? 1 : 0)} />
            </div>
        </div>
    );
};

export default CCMCodingPanel;
